import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { colors, fonts, KANBAN_ITEM_TYPES } from "../styles/theme";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Monday start
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CalendarGrid({ items = [], onItemClick, onDateClick, view = "month" }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [currentDate, setCurrentDate] = useState(new Date(today));
  const [viewMode, setViewMode] = useState(view);

  // Group items by date
  const itemsByDate = useMemo(() => {
    const map = {};
    for (const item of items) {
      if (!item.dueDate) continue;
      const d = new Date(item.dueDate);
      const key = dateKey(d);
      if (!map[key]) map[key] = [];
      map[key].push(item);
    }
    return map;
  }, [items]);

  // Generate grid dates
  const gridDates = useMemo(() => {
    if (viewMode === "week") {
      const start = startOfWeek(currentDate);
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return d;
      });
    }
    // Month view
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const start = startOfWeek(firstOfMonth);
    const dates = [];
    const d = new Date(start);
    // Always show 6 weeks for consistent grid
    for (let i = 0; i < 42; i++) {
      dates.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return dates;
  }, [currentDate, viewMode]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === "week") {
      d.setDate(d.getDate() + dir * 7);
    } else {
      d.setMonth(d.getMonth() + dir);
    }
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date(today));

  const headerLabel = viewMode === "week"
    ? `Week of ${gridDates[0]?.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
    : `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <button onClick={() => navigate(-1)} style={navBtnStyle}><ChevronLeft size={16} /></button>
        <button onClick={() => navigate(1)} style={navBtnStyle}><ChevronRight size={16} /></button>
        <span style={{ fontSize: 16, fontWeight: 700, color: colors.text, fontFamily: fonts.heading }}>{headerLabel}</span>
        <button onClick={goToday} style={{ ...navBtnStyle, fontSize: 11, padding: "4px 10px" }}>Today</button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
          {["month", "week"].map((m) => (
            <button
              key={m}
              onClick={() => setViewMode(m)}
              style={{
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 600,
                border: `1px solid ${colors.border}`,
                borderRadius: 4,
                background: viewMode === m ? colors.accent : "transparent",
                color: viewMode === m ? "#fff" : colors.textSecondary,
                cursor: "pointer",
                fontFamily: fonts.body,
                textTransform: "capitalize",
              }}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Day labels */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1 }}>
        {DAY_LABELS.map((d) => (
          <div key={d} style={{ padding: "4px 6px", fontSize: 10, fontWeight: 700, color: colors.textSecondary, textTransform: "uppercase", textAlign: "center", fontFamily: fonts.body }}>
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, border: `1px solid ${colors.border}`, borderRadius: 6, overflow: "hidden" }}>
        {gridDates.map((date, i) => {
          const key = dateKey(date);
          const dayItems = itemsByDate[key] || [];
          const isToday = isSameDay(date, today);
          const isCurrentMonth = date.getMonth() === currentDate.getMonth();
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;

          return (
            <div
              key={i}
              onClick={() => onDateClick?.(date)}
              style={{
                minHeight: viewMode === "week" ? 200 : 90,
                background: isToday ? "rgba(99,102,241,0.04)" : isWeekend ? "rgba(0,0,0,0.02)" : colors.bgCard,
                padding: 4,
                cursor: "pointer",
                borderRight: (i + 1) % 7 !== 0 ? `1px solid ${colors.borderLight || colors.border}` : "none",
                borderBottom: i < gridDates.length - 7 ? `1px solid ${colors.borderLight || colors.border}` : "none",
              }}
            >
              <div style={{
                fontSize: 11,
                fontWeight: isToday ? 700 : 400,
                color: isToday ? "#6366f1" : isCurrentMonth ? colors.text : colors.textSecondary,
                marginBottom: 2,
                textAlign: "right",
                padding: "0 2px",
              }}>
                {isToday ? (
                  <span style={{ background: "#6366f1", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>
                    {date.getDate()}
                  </span>
                ) : date.getDate()}
              </div>
              {dayItems.slice(0, viewMode === "week" ? 10 : 3).map((item) => {
                const typeInfo = KANBAN_ITEM_TYPES[item.type] || KANBAN_ITEM_TYPES.task;
                return (
                  <div
                    key={item.id}
                    onClick={(e) => { e.stopPropagation(); onItemClick?.(item); }}
                    style={{
                      fontSize: 10,
                      padding: "2px 4px",
                      marginBottom: 1,
                      borderRadius: 3,
                      background: typeInfo.bg,
                      color: typeInfo.color,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      fontFamily: fonts.body,
                    }}
                    title={item.title}
                  >
                    {item.dueDate && (
                      <span style={{ opacity: 0.7, marginRight: 3 }}>
                        {new Date(item.dueDate).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                    {item.title}
                  </div>
                );
              })}
              {dayItems.length > (viewMode === "week" ? 10 : 3) && (
                <div style={{ fontSize: 9, color: colors.textSecondary, padding: "1px 4px" }}>
                  +{dayItems.length - (viewMode === "week" ? 10 : 3)} more
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const navBtnStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: 6,
  border: `1px solid ${colors.border}`,
  background: "transparent",
  color: colors.text,
  cursor: "pointer",
};
